import { createAction } from 'redux-actions';
import { StatusCodes } from 'http-status-codes';
import {
  autoTieoutForTrialBalanceRequest,
  getTrailBalanceWorkFlowRequest,
  getWorkpaperByTrialBalanceStatement,
  getTrialBalanceMapedElements,
  mapElementsInTrialBalance,
  setOmniaTagAndMapElements,
  unlinkOmniaTagAndMapElements,
  trialBalanceRetryRequest,
  setOmniaAutoTagAndMapElements,
  unlinkWPReferencesInBatch,
} from 'api/trial-balance-api';
import { importOmniaDetails } from 'api/statement-api';
import statusCodes from 'http-status-codes';
import {
  setAutoTieoutStatusForBanner,
  setAutoTieoutWorkpaperReference,
} from '../auto-tieout/workpaper-details-action';
import {
  replaceSelectedElementWithoutRemovingOld,
  clearSideBySideElementsMap,
  clearSelectedElementsContentPanel,
  clearingElementMapSuccessAction,
} from 'store/actions/action-creators';
import {
  trialBalanceMapedElementsLoaded,
  fetchTrialBalanceMapedElements,
  resetMappedElements,
} from 'store/actions/trial-balance/mapped-element-action';
import {
  reFetchElementsForSectionsInView,
  fetchAutoTieoutMappedElementByMode,
} from 'store/actions/statement-content-actions';
import { getElementDetailsRequest } from 'api/element-api';
import ElementDetails from 'models/api/element-details-api-model';
import { updateElementInCacheAction } from 'store/actions/element-cache-actions';
import { disableAllElementsCallout } from 'store/actions/statement-content-annotation-creation-actions';
import { removeAutoTieoutWorkpaperReference } from 'store/actions/auto-tieout/workpaper-details-action';
import {
  setTrialBalanceSnapshotViewLoaded,
  setSnapshotReadOnly,
} from 'store/actions/trial-balance/trial-balance-snapshot-view-action';
import { resetSideBySideViewMode } from 'store/actions/side-by-side-view-annotations-actions';
import { clearSourceStatementParams } from 'store/actions/source-statement-actions';
import { setSourceStatementLoaded } from 'store/actions/load-source-statement-actions';
import { clearBatchSelectedElementsAction } from 'store/actions/action-creators';
import {
  AUTO_TIEOUT_COMPARE_MODE_FAILED,
  AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
  AUTO_TIEOUT_COMPARE_MODE_SUCCESS,
  TRIAL_BALANCE_PROCESSING_STATUS,
} from 'constants/feature/statement-content-constants';
import {
  UNLINK_REFERENCE,
  UNTAG_ELEMENT,
} from 'constants/feature/element-panel-constants';
import { ELEMENT_HIGHLIGHT_STATES } from 'constants/feature/tieout-element-constants';
import {
  TRIAL_BALANCE_SCALING_OPTIONS_MAP,
  TB_PENDING_STEPS,
} from 'constants/feature/statement-creation-constants';
import { getTrialBalanceTaggedElementsData } from './tagged-element-action';
import { getUrlParam } from 'utils/project-utils';
import { setAnnotationDisplayOption } from 'store/actions/annotation-display-actions';
import { cancelBatchUpdatePanel } from 'store/actions/batch-panel-actions';
import { clearModeIfPossible } from 'store/actions/modes-actions';

export const setThreshold = createAction('SET_THRESHOLD');
export const setScaling = createAction('SET_SCALING');
export const resetThresholdScaling = createAction('RESET_THRESHOLD_SCALING');

export const setHighlightMappedElements = createAction(
  'SET_HIGHLIGHT_MAPPED_ELEMENTS',
);
export const resetHighlightMappedElements = createAction(
  'RESET_HIGHLIGHT_MAPPED_ELEMENTS',
);
export const trailBalanceWorkFlowStatusLoaded = createAction(
  'TRAIL_BALANCE_WORKFLOW_STATUS_LOADED',
);

export const setTrialBalanceTableTab = createAction(
  'SET_TRIAL_BALANCE_TABLE_TAB',
);

export const setElementToScrollInTrialBalanceView = createAction(
  'SET_ELEMENT_TO_SCROLL_IN_TRIAL_BALANCE_VIEW',
);

export const trailBalanceWorkFlowStatusLoading = createAction(
  'TRAIL_BALANCE_WORKFLOW_STATUS_LOADING',
);

export const trailBalanceWorkFlowStatusError = createAction(
  'TRAIL_BALANCE_WORKFLOW_STATUS_ERROR',
);

export const resetTrailBalanceWorkFlowStatus = createAction(
  'RESET_TRAIL_BALANCE_WORKFLOW_STATUS',
);

export const setTrialBalanceRowHighlight = createAction(
  'SET_TRIAL_BALANCE_ROW_HIGHLIGHT',
);
export const clearTrialBalanceRowHighlight = createAction(
  'CLEAR_TRIAL_BALANCE_ROW_HIGHLIGHT',
);
export const deselectTrialBalanceRowHighlight = createAction(
  'DESELECT_TRIAL_BALANCE_ROW_HIGHLIGHT',
);
export const omniaTBUpdateAlert = createAction('OMNIA_TB_UPDATE_ALERT');

export const updatePendingAutoTieoutTBStep = createAction(
  'UPDATE_PENDING_AUTO_TIEOUT_TRIAL_BALANCE_STEP',
);

// user to initiate auto tieout process for trial balance
export const initiateAutoTieoutForTrialBalance =
  (statementId, revisionId, thresholdScalingValues, workpaperId, tbVersionId) =>
  async (dispatch, getState) => {
    const { data, ui } = getState();
    const {
      selectedProject: { id },
    } = data;

    const {
      sideBySideView: {
        trialBalance: { tbWorkFlowStatus },
      },
    } = ui;
    try {
      dispatch(
        setAutoTieoutStatusForBanner({
          status: AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
        }),
      );
      const response = await autoTieoutForTrialBalanceRequest({
        clientId: parseInt(id),
        statementId: parseInt(statementId),
        revisionId: parseInt(revisionId),
        scaling: parseInt(thresholdScalingValues?.scaling?.value),
        threshold: parseInt(thresholdScalingValues?.threshold),
        workpaperId,
        tbVersionId: tbWorkFlowStatus?.stale ? null : tbVersionId, // Pass tbVersionId as null if the workflow is stale to force creation of new TB version in auto tieout process
      });
      if (response?.status === statusCodes.OK) {
        await dispatch(getTrialBalanceDetails(false));
      }
    } catch (error) {
      console.error(
        'Auto tieout for trial balance request failed with error: ',
        error,
      );
      dispatch(
        setAutoTieoutStatusForBanner({
          status: AUTO_TIEOUT_COMPARE_MODE_FAILED,
        }),
      );
    } finally {
      dispatch(clearingElementMapSuccessAction());
      await dispatch(clearSideBySideElementsMap());
      await dispatch(resetMappedElements());
      await dispatch(cancelBatchUpdatePanel());
      await dispatch(clearModeIfPossible());
      await dispatch(clearSelectedElementsContentPanel());
      await dispatch(clearBatchSelectedElementsAction());
    }
  };

export const retryTrialBalance = () => async (dispatch, getState) => {
  const { ui, data } = getState();
  const {
    sideBySideView: {
      trialBalance: { tbWorkFlowStatus, thresholdScaling },
    },
  } = ui;
  const {
    revision,
    selectedProject: { id },
    selectedStatement: { statementId },
  } = data;

  const hasWorkflowData =
    tbWorkFlowStatus?.status !== '' && tbWorkFlowStatus?.status !== undefined;

  if (!hasWorkflowData) {
    return;
  }

  dispatch(
    setAutoTieoutStatusForBanner({
      status: AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
    }),
  );

  try {
    const requestBody = {
      clientId: parseInt(id),
      statementId: parseInt(statementId),
      revisionId: parseInt(revision?.id),
      scaling: parseInt(
        thresholdScaling?.scaling?.value || tbWorkFlowStatus.scalingId,
      ),
      threshold: parseInt(
        thresholdScaling?.threshold || tbWorkFlowStatus.valueThreshold,
      ),
      workpaperId: parseInt(tbWorkFlowStatus.workpaperId),
      tbVersionId: parseInt(tbWorkFlowStatus.tbVersionId),
    };

    await trialBalanceRetryRequest(requestBody);
  } catch (error) {
    console.error('Trial balance retry request failed with error: ', error);
    dispatch(
      setAutoTieoutStatusForBanner({ status: AUTO_TIEOUT_COMPARE_MODE_FAILED }),
    );
  }
};

export const getLatestTrialBalanceWorkflows =
  () => async (dispatch, getState) => {
    const { data } = getState();
    const {
      revision,
      selectedProject: { id },
      selectedStatement: { statementId },
    } = data;

    // Use Redux state if available, otherwise extract from URL
    const { urlStatementId, urlProjectId, urlRevisionId } = getUrlParam();
    const clientId = parseInt(id || urlProjectId);
    const stmtId = parseInt(statementId || urlStatementId);
    const revId = parseInt(revision?.id || urlRevisionId);

    let response;
    try {
      dispatch(trailBalanceWorkFlowStatusLoading());
      response = await getTrailBalanceWorkFlowRequest({
        clientId,
        statementId: stmtId,
        revisionId: revId,
      });
      if (response?.status === statusCodes.OK) {
        dispatch(trailBalanceWorkFlowStatusLoaded({ response }));
        dispatch(setThreshold(response?.data?.valueThreshold));
        const scalingValue = TRIAL_BALANCE_SCALING_OPTIONS_MAP.get(
          response?.data?.scalingId,
        );
        dispatch(setScaling(scalingValue));

        const workflowStatus = response?.data?.status;
        if (workflowStatus === TRIAL_BALANCE_PROCESSING_STATUS.COMPLETED) {
          dispatch(
            setAutoTieoutStatusForBanner({
              status: AUTO_TIEOUT_COMPARE_MODE_SUCCESS,
            }),
          );
        } else if (workflowStatus === TRIAL_BALANCE_PROCESSING_STATUS.FAILED) {
          dispatch(
            setAutoTieoutStatusForBanner({
              status: AUTO_TIEOUT_COMPARE_MODE_FAILED,
            }),
          );
        } else if (
          workflowStatus === TRIAL_BALANCE_PROCESSING_STATUS.IN_PROGRESS ||
          workflowStatus === TRIAL_BALANCE_PROCESSING_STATUS.NEW
        ) {
          dispatch(
            setAutoTieoutStatusForBanner({
              status: AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
            }),
          );
        }
      }
    } catch (error) {
      console.error(
        'Get latest trial balance workflows request failed with error: ',
        error,
      );
      dispatch(trailBalanceWorkFlowStatusError({ error }));
    }

    // to fetch workpaper details for trial balance auto tieout process workpaper
    if (response?.data?.workpaperId) {
      try {
        const wpResponse = await getWorkpaperByTrialBalanceStatement({
          revisionId: revision.id,
          statementId,
          workPaperId: response?.data?.workpaperId,
        });
        // to update the auto tieout workpaper reference in the store after getting response from the workpaper get api
        if (wpResponse?.data?.result) {
          dispatch(setAutoTieoutWorkpaperReference(wpResponse));
        }
      } catch (err) {
        console.error('Auto tieout process workpaper error', err);
      }
    }

    // Return the workflow data so it can be used by the caller
    return response;
  };

export const getTrialBalanceDetails =
  (fetchMappedCount = true) =>
  async (dispatch, getState) => {
    try {
      const { data, ui, sockets } = getState();
      const {
        revision,
        selectedStatement: { statementId, isOCR },
        selectedProject: { id },
        annotationDisplayOptions,
      } = data;

      const {
        statementPage: { sectionsInView },
      } = ui;

      const {
        statementSocket: { socketHasBeenDisconnected },
      } = sockets;

      // Get the trial balance workflow data directly from the action
      const trialBalanceWorkflow = await dispatch(
        getLatestTrialBalanceWorkflows(),
      );

      // Fetch tagged elements data for trial balance
      await dispatch(getTrialBalanceTaggedElementsData());

      const status = trialBalanceWorkflow?.data?.status;

      if (status === TRIAL_BALANCE_PROCESSING_STATUS.COMPLETED) {
        if (socketHasBeenDisconnected) {
          const loadedSectionsInView = Object.keys(sectionsInView);
          dispatch(
            reFetchElementsForSectionsInView(loadedSectionsInView, isOCR),
          );

          // dispatch expand All annotations on success
          dispatch(
            setAnnotationDisplayOption({
              ...annotationDisplayOptions,
              showAll: true,
            }),
          );
        }

        // Fetch mapped elements for trial balance
        dispatch(
          fetchTrialBalanceMapedElements({
            clientId: parseInt(id),
            statementId,
            revisionId: revision?.id,
          }),
        );
      }
    } catch (error) {
      console.error('Get trial balance details failed with error: ', error);
    }
  };

export const getTBMappedElementsOnAutoTieoutCompleted =
  ({ clientId, statementId, revisionId }) =>
  async (dispatch, getState) => {
    const socketHasBeenDisconnected =
      getState()?.sockets?.statementSocket?.socketHasBeenDisconnected;

    try {
      const response = await getTrialBalanceMapedElements({
        statementId,
        revisionId,
        clientId,
      });
      // Todo: need to handle target(key) elements when TB auto tieout
      dispatch(trialBalanceMapedElementsLoaded({ response }));
      const { elements } = (response?.data || []).reduce(
        (acc, item) => {
          acc.elements.push(item.elementId);
          return acc;
        },
        { elements: [] },
      );
      // highlighting mapped elements
      dispatch(
        replaceSelectedElementWithoutRemovingOld({
          elementIds: elements,
          color: ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_SUCCESS,
        }),
      );
    } catch (e) {
      console.error('fetching Mapped elements failed with error', e);
    }
  };

export const performManualMappingInTrialBalance =
  ({ statementParams }) =>
  async (dispatch, getState) => {
    const currentSideBySideElementMap =
      getState()?.ui?.sideBySideView?.sideBySideElementMap;

    if (!currentSideBySideElementMap) {
      return;
    }

    const sizeOfSourceMapping = currentSideBySideElementMap.sizeOfSourceMapping;
    const sizeOfTargetMapping = currentSideBySideElementMap.sizeOfTargetMapping;

    if (!sizeOfSourceMapping || !sizeOfTargetMapping) {
      return;
    }

    const [tbRowDetails] =
      currentSideBySideElementMap.sourceStatementElementMap.values();
    const [targetElementDetails] =
      currentSideBySideElementMap.targetStatementElementMap.values();

    const trialBalanceMappedElements =
      getState()?.ui?.sideBySideView?.trialBalance?.mappedElements;

    const isSourceAlreadyMapped =
      trialBalanceMappedElements?.getSourceMapping?.(tbRowDetails?.id);
    if (isSourceAlreadyMapped && isSourceAlreadyMapped.active) {
      return;
    }

    const isTargetAlreadyMapped =
      trialBalanceMappedElements?.getTargetMapping?.(targetElementDetails?.id);
    if (isTargetAlreadyMapped && isTargetAlreadyMapped.active) {
      return;
    }

    const trialBalanceState = getState()?.ui?.sideBySideView?.trialBalance;
    const thresholdScaling = trialBalanceState?.thresholdScaling;
    const tbVersionId = trialBalanceState?.tbWorkFlowStatus?.tbVersionId;
    const workpaper =
      getState()?.ui?.sideBySideView?.autoTieout?.workpaperDetails;

    if (!workpaper?.isLoaded) {
      return;
    }
    const socketHasBeenDisconnected =
      getState()?.sockets?.statementSocket?.socketHasBeenDisconnected;

    try {
      const { projectId, statementId, revisionId } = statementParams;
      const scalingValue = thresholdScaling?.data?.scaling?.value || null;

      const wp = workpaper.data;

      const trialBalanceMappingPayload = {
        elementId: parseInt(targetElementDetails.id),
        tbDataId: parseInt(tbRowDetails.id),
        tbElementDisplay:
          tbRowDetails.amount != null ? String(tbRowDetails.amount) : '',
        tbElementScalingId: scalingValue,
        tbVersionId: parseInt(tbVersionId),
        workpaperReferenceDto: {
          revisionId: wp.revisionId,
          elementId: parseInt(targetElementDetails.id),
          workpaperRefId: wp.workpaperRefId,
          referenceNumber: wp.referenceNumber,
          referenceName: wp.referenceName || '',
          workpaperRefCreatedBy: wp.workpaperRefCreatedBy,
          workpaperRefCreatedDate: wp.workpaperRefCreatedDate,
          elementWpRefCreatedBy: wp.elementWpRefCreatedBy,
          elementWpRefCreatedDate: wp.elementWpRefCreatedDate,
          elementCount: wp.elementCount || 0,
          creationType: 'MANUAL',
          overrideCreationType: wp.overrideCreationType || false,
        },
      };

      const response = await mapElementsInTrialBalance({
        clientId: parseInt(projectId),
        statementId: parseInt(statementId),
        revisionId: parseInt(revisionId),
        mappingPayload: trialBalanceMappingPayload,
      });

      if (response.status === statusCodes.OK) {
        const elementResponse = await getElementDetailsRequest({
          revisionId: parseInt(revisionId),
          elementId: targetElementDetails.id,
        });
        const elementToUpdate = new ElementDetails();
        const elementWithCalloutDetails =
          elementToUpdate.setLoadedFromElementData({
            data: {
              ...elementResponse.data.result,
              callout: true,
              marker: false,
            },
          });
        dispatch(
          updateElementInCacheAction({ element: elementWithCalloutDetails }),
        );

        dispatch(
          setTrialBalanceRowHighlight({
            elementIds: [tbRowDetails.id],
            color: ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_SUCCESS,
          }),
        );

        dispatch(
          replaceSelectedElementWithoutRemovingOld({
            elementIds: [targetElementDetails.id],
            color: ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_SUCCESS,
          }),
        );

        dispatch(
          fetchTrialBalanceMapedElements({
            clientId: parseInt(projectId),
            statementId: parseInt(statementId),
            revisionId: parseInt(revisionId),
            withoutLoading: true,
          }),
        );
      }
    } catch (error) {
      dispatch(
        setTrialBalanceRowHighlight({
          elementIds: [tbRowDetails.id],
          color: ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_FAILURE,
        }),
      );

      dispatch(
        replaceSelectedElementWithoutRemovingOld({
          elementIds: [targetElementDetails.id],
          color: ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_FAILURE,
        }),
      );
    } finally {
      dispatch(clearSideBySideElementsMap());
    }

    //on any key press or left click it will reset the successfully or failed mapped elements highlight to the default
    const clearHighlights = () => {
      document.removeEventListener('keydown', clearHighlights, true);
      document.removeEventListener('click', clearHighlights, true);
      dispatch(clearSelectedElementsContentPanel());
      dispatch(clearTrialBalanceRowHighlight());
    };
    document.addEventListener('keydown', clearHighlights, {
      capture: true,
      once: true,
    });
    document.addEventListener('click', clearHighlights, {
      capture: true,
      once: true,
    });
  };

export const performTBAutoTieoutOnUpdatedSnapshot =
  ({ clientId, statementId, revisionId }) =>
  async (dispatch) => {
    try {
      dispatch(
        setAutoTieoutStatusForBanner({
          status: AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
        }),
      );
      const response = await importOmniaDetails({
        clientId,
        statementId,
        revisionId,
      });
      if (response?.status === StatusCodes.CREATED) {
        //based on this action flag, we will fetch the latest snapshot data in trial-balance-snapshot-view.jsx file
        dispatch(
          updatePendingAutoTieoutTBStep(
            TB_PENDING_STEPS.FETCH_UPDATED_SNAPSHOT,
          ),
        );
      }
    } catch (e) {
      dispatch(updatePendingAutoTieoutTBStep(TB_PENDING_STEPS.NONE));
      dispatch(
        setAutoTieoutStatusForBanner({
          status: AUTO_TIEOUT_COMPARE_MODE_FAILED,
          show: false,
        }),
      );
      console.error('Failed to import updated omnia details', e);
    }
  };

export const handleOmniaTagAndMapElements =
  ({ balanceSheetData, type, sourceStatementParams }) =>
  async (dispatch, getState) => {
    let operationName;
    try {
      let apiCall;

      // Determine which API to call based on type
      if (type === UNLINK_REFERENCE) {
        apiCall = unlinkWPReferencesInBatch;
        operationName = 'Unlink reference';
      } else if (type === UNTAG_ELEMENT) {
        apiCall = unlinkOmniaTagAndMapElements;
        operationName = 'Untag elements';
      } else {
        // Default: set/map elements (for BS_WP, IS_WP)
        apiCall = setOmniaTagAndMapElements;
        operationName = 'Tag and map elements';
      }

      const response = await apiCall({ balanceSheetData });

      if (response?.status === statusCodes.OK) {
        // Map each type to its corresponding action with parameters
        const actionMap = {
          [UNLINK_REFERENCE]: () => {
            const socketHasBeenDisconnected =
              getState()?.sockets?.statementSocket?.socketHasBeenDisconnected;
            if (socketHasBeenDisconnected) {
              return dispatch(
                fetchAutoTieoutMappedElementByMode({
                  clientId: balanceSheetData.clientId,
                  sourceStatementId: sourceStatementParams?.sourceStatementId,
                  targetStatementId: balanceSheetData.statementId,
                  sourceRevisionId: sourceStatementParams?.sourceRevisionId,
                  targetRevisionId: balanceSheetData.revisionId,
                  withoutLoading: true,
                }),
              );
            }
          },
          [UNTAG_ELEMENT]: () => getTrialBalanceTaggedElementsData(),
          default: () => getTrialBalanceTaggedElementsData(),
        };

        const actionCreator = actionMap[type] || actionMap.default;

        if (actionCreator) {
          const action = actionCreator();
          // Only dispatch if the handler returned a dispatchable action/thunk
          if (action) {
            await dispatch(action);
          }
        }
      }
    } catch (error) {
      console.error(
        `${operationName || 'Operation'} failed with error:`,
        error,
      );
      throw error;
    }
  };

/**
 * Resets the trial balance view state including scroll position,
 * workpaper references, and view mode settings
 * @param {Object} syncScrollIds - Object containing targetId for scroll element
 */
export const resetTrialBalanceView =
  ({ syncScrollIds }) =>
  (dispatch) => {
    // Reset scroll position to top
    const scrollElement = document.getElementById(syncScrollIds.targetId);
    if (scrollElement) {
      scrollElement.scrollTop = 0;
    }
    dispatch(resetMappedElements());
    dispatch(clearSideBySideElementsMap());
    dispatch(disableAllElementsCallout({ showCallout: false }));
    dispatch(removeAutoTieoutWorkpaperReference());
    dispatch(setTrialBalanceSnapshotViewLoaded(false));
    dispatch(setSnapshotReadOnly(false));
    dispatch(clearSourceStatementParams());
    dispatch(setSourceStatementLoaded(false));
    dispatch(clearSelectedElementsContentPanel());
    dispatch(clearBatchSelectedElementsAction());
    dispatch(resetTrailBalanceWorkFlowStatus());
    dispatch(resetThresholdScaling());
    dispatch(resetSideBySideViewMode());
  };

export const omniaAutoTagAndMapElements = () => async (dispatch, getState) => {
  const { data, ui } = getState();
  const {
    revision,
    selectedProject: { id },
    selectedStatement: { statementId, isOCR },
  } = data;

  const {
    statementPage: {
      sectionsInView: { sectionIds: loadedSectionsInView },
    },
  } = ui;

  // Use Redux state if available, otherwise extract from URL
  const { urlStatementId, urlProjectId, urlRevisionId } = getUrlParam();
  const clientId = parseInt(id || urlProjectId);
  const stmtId = parseInt(statementId || urlStatementId);
  const revId = parseInt(revision?.id || urlRevisionId);

  try {
    await setOmniaAutoTagAndMapElements({
      clientId,
      statementId: stmtId,
      revisionId: revId,
    });
    await dispatch(
      reFetchElementsForSectionsInView(loadedSectionsInView, isOCR),
    );
  } catch (error) {
    console.error('Auto tag and map elements failed with error: ', error);
  }
};
